/* We separate out the *build-time* `process.env` processing from the *run-time* processing for a few reasons:
 *
 * 1. Type checking and schema enforcement needs to happen at build time
 * 2. We don't want/need to bundle zod in with the client JS
 */
import { CLIENT_ENV } from '@lib/env';
import { SafeUrl } from '@types';

const isProduction = () => {
  return CLIENT_ENV.NEXT_PUBLIC_NODE_ENV === 'production';
};

// Note: the following will use the production app when running next.js as a
// production build. e.g. `next build && next start`
const getAppOrigin = () => {
  if (isProduction()) {
    return 'https://app.wistia.com';
  }
  return 'https://app.wistia.io';
};

export const APP_ORIGIN = getAppOrigin();

export const IS_PRODUCTION = isProduction();

export const GTM_ID = 'GTM-WJT32MJ';

export const EV1_URL = 'https://fast.wistia.com/assets/external/E-v1.js';

export const WISTIA_SESSION_COOKIE_NAME = `_w_session`;

export const WISTIA_GIT = '/pricing/getintouch';

export const WISTIA_GIT_PREMIUM = '/pricing/getintouch?plan=premium';

/**
 * Every URL here goes through the VMA, with all redirect logic
 * happening VMA-side (e.g. for customers who are already logged in, or have an
 * account of some kind, etc.)
 */

// Note: current and in-use as of P&P 2026
export const WISTIA_FREE_ACCOUNT =
  'https://app.wistia.com/auth/wistia_auth_register_pre_auth?plan_type=free';
export const WISTIA_BUSINESS_TRIAL =
  'https://app.wistia.com/auth/wistia_auth_register_pre_auth?plan_type=business_trial';
export const WISTIA_BUSINESS_WEBINARS_BASIC_TRIAL =
  'https://app.wistia.com/auth/wistia_auth_register_pre_auth?plan_type=business_webinars_basic_trial';
export const WISTIA_BUSINESS_WEBINARS_PLUS_TRIAL =
  'https://app.wistia.com/auth/wistia_auth_register_pre_auth?plan_type=business_webinars_plus_trial';
export const WISTIA_BUSINESS_AUTOMATION_TRIAL =
  'https://app.wistia.com/auth/wistia_auth_register_pre_auth?plan_type=business_automation_trial';
export const WISTIA_DEVELOPER_ACCOUNT =
  'https://app.wistia.com/auth/wistia_auth_register_pre_auth?plan_type=developer';

export const HUBSPOT_PORTAL_ID = '120237';

// HubSpot form name: [REPORT] State of Video 2025 - Updated
export const HUBSPOT_STATE_OF_VIDEO_2025_FORM_ID =
  'defac7ea-15d4-4a4a-9202-4934b6d0712e';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website) - Updated [white text for Storefront testing]
// This form has white text meant for a dark background
export const HUBSPOT_TEST_FORM_WHITE_TEXT_ID =
  'ee852e96-947c-4c5f-8b93-6befeb204f7e';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website) - Updated
export const HUBSPOT_TEST_FORM_ID = 'd167e04c-0205-4f19-81ec-3c55d1d9c5b5';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website)
export const HUBSPOT_GIT_SALES_FORM_ID = 'c59d4834-0b02-4275-aac0-6cc1e7599395';

// HubSpot form name: [Marketing] Webinar Guidebook LP - Updated
export const HUBSPOT_WEBINAR_GUIDEBOOK_FORM_ID =
  'c487b8c1-9adf-421e-bca9-f9487ebdc134';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website) - Updated
export const HUBSPOT_GIT_SALES_FORM_UPDATED_ID =
  'd167e04c-0205-4f19-81ec-3c55d1d9c5b5';

// HubSpot form name: Request an Integration Form
// This form has white text meant for a dark background
export const HUBSPOT_REQUEST_INTEGRATION_FORM_ID =
  'eb403831-5c70-4b92-b76f-59c60061baf2';

export const CHROMATIC_DEFAULT_DELAY = 500;
export const CHROMATIC_VIDEO_DELAY = 3000;
/**
 * ## Recommended Thresholds by Component Type
 * | Component Type | Recommended `diffThreshold` | Rationale |
 * |----------------|----------------------------|-----------|
 * | Static UI (buttons, cards) | `0.0` - `0.1` | Should be pixel-perfect |
 * | Images with filters | `0.1` - `0.2` | Minor compression differences |
 * | Videos | `0.3` - `0.4` | Player controls, thumbnails vary |
 * | Charts/Graphs | `0.2` - `0.3` | Data visualization quirks |
 * | Animations (paused) | `0.1` - `0.2` | Anti-aliasing differences |
 * | Complex layouts | `0.15` - `0.25` | Multiple dynamic elements |
 */
export const CHROMATIC_DEFAULT_DIFF_THRESHOLD = 0.1; // Chromatic's default is .063
export const CHROMATIC_IMAGE_DIFF_THRESHOLD = 0.15;
export const CHROMATIC_VIDEO_DIFF_THRESHOLD = 0.35; // video player is external package, and often has updated UI changes

export const MOBILE_NAVBAR_OFFSET_WITH_HOMEPAGE_PROMO = 145;

export const MOBILE_NAVBAR_OFFSET_DEFAULT = 60;

const META_IMAGE_QUERY_PARAM = '?w=1200&h=630&fit=fill&fl=progressive';

/**
 * Appends necessary parameters of Contentful's Image API parameters to resize, crop, and load a progressive JPEG image to improve image loading performance.
 * - Used for the META_IMAGE variable declared at the top of each page.
 *
 * @param imageUrl - A URL of a meta image uploaded and published with Contentful.
 * @returns URL with Contentful Image API paramaters appended to the end of the URL.
 *
 * @example
 * ```typescript
 * // Format the URL for meta images used in page metadata.
 * const META_IMAGE = buildMetaImageUrl('https://images.ctfassets.net/j7pfe8y48ry3/Pricing_MetaImage.jpg');
 * 
 * // Add the META_IMAGE URL to page metadata.
 * export const metadata: Metadata = {
  ... 
  openGraph: {
    ...
    images: [META_IMAGE],
  },
  twitter: {
    ...
    images: [META_IMAGE],
    },
  };
 * ```
 */
export const buildMetaImageUrl = (imageUrl: SafeUrl) => {
  return `${imageUrl}${META_IMAGE_QUERY_PARAM}`;
};

export const GBUILDS = String.raw`


  __                                                                    __   __   __   __ 
|/  |         | /           /            |       |                     /  ) /  | /  ) /   
|   | ___  ___|   ___  ___ (___  ___  ___|       |      ___  ___      (  / (   |(  / |___ 
|   )|___)|   )| |    |   )|    |___)|   )       )|   )|   )|___)       / )|   )  / )    )
|__/ |__  |__/ | |__  |__/||__  |__  |__/      _/ |__/ |  / |__        /_/ |__/  /_/  __/ 

                                                                                          

  ██████╗  ███████╗ ██████╗ ██████╗ ██╗███████╗    ███████╗███╗   ███╗██╗████████╗██╗  ██╗
  ██╔════╝ ██╔═══██╗██╔══██╗██╔══██╗██║██╔════╝    ██╔════╝████╗ ████║██║╚══██╔══╝██║  ██║
  ██║  ███╗██║   ██║██████╔╝██║  ██║██║█████╗      ███████╗██╔████╔██║██║   ██║   ███████║
  ██║   ██║██║   ██║██╔══██╗██║  ██║██║██╔══╝      ╚════██║██║╚██╔╝██║██║   ██║   ██╔══██║
  ╚██████╔╝╚██████╔╝██║  ██║██████╔╝██║███████╗    ███████║██║ ╚═╝ ██║██║   ██║   ██║  ██║
   ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═╝╚══════╝    ╚══════╝╚═╝     ╚═╝╚═╝   ╚═╝   ╚═╝  ╚═╝
                                                 
  
    
   ___                _    __                                           __                                     
  /                  /|   /  |                                         /         /         / /    | /          
(___  ___  ___      ( |  (   |           ___  ___  ___  ___       ___ (         (___        (  ___|   ___  ___ 
|    |   )|   )       | )|   )     \   )|___)|   )|   )|___      |   )|___      |   )|   )| | |   )| |   )|   )
|    |__/ |          _|/ |__/       \_/ |__  |__/||     __/      |__/ |         |__/ |__/ | | |__/ | |  / |__/ 
                                     /                                                                    __/ 
                                     
    

    `;
